import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../ProductCard/ProductCard';
import { useProducts } from '../../contexts/ProductsContext';
import './ProductGrid.css';

const SORT_OPTIONS = [
  { label: 'Menor Valor', value: 'price_asc' },
  { label: 'Maior Valor', value: 'price_desc' },
  { label: 'Nome A-Z', value: 'name_asc' },
];

const applyFilters = (products, activeFilters) => {
  if (!activeFilters) return products;
  const { sizes = [], colors = [], categories = [] } = activeFilters;
  if (!sizes.length && !colors.length && !categories.length) return products;

  return products.filter((p) => {
    if (sizes.length > 0 && !sizes.some((s) => (p.sizesAvailable || []).includes(s))) return false;
    if (colors.length > 0 && !colors.includes(p.metadata?.color)) return false;
    if (categories.length > 0 && !categories.includes(p.metadata?.category)) return false;
    return true;
  });
};

const applySort = (products, sort) => {
  const list = [...products];
  if (sort === 'price_asc') return list.sort((a, b) => a.priceValue - b.priceValue);
  if (sort === 'price_desc') return list.sort((a, b) => b.priceValue - a.priceValue);
  if (sort === 'name_asc') return list.sort((a, b) => a.name.localeCompare(b.name));
  return list;
};

const ProductGrid = ({
  showTitle = 'Coleções em destaque',
  showAllButton = true,
  productCount,
  activeFilters,
}) => {
  const { products, loading, error } = useProducts();
  const [sort, setSort] = useState('price_asc');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(
    () => applySort(applyFilters(products, activeFilters), sort),
    [products, activeFilters, sort]
  );

  const list = productCount != null ? filtered.slice(0, productCount) : filtered;
  const currentLabel = SORT_OPTIONS.find((o) => o.value === sort)?.label || 'Ordenar';

  if (loading) {
    return (
      <section className="product-grid-section">
        <div className="product-grid-header">
          <h2 className="section-title">{showTitle}</h2>
        </div>
        <div className="product-grid-loading">Carregando produtos...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="product-grid-section">
        <div className="product-grid-header">
          <h2 className="section-title">{showTitle}</h2>
        </div>
        <div className="product-grid-error">{error}</div>
      </section>
    );
  }

  return (
    <section className="product-grid-section">
      <div className="product-grid-header">
        <h2 className="section-title">{showTitle}</h2>
        <div className="product-grid-actions">
          {showAllButton && (
            <Link to="/catalog" className="btn btn-outline">
              <span>Ver Tudo</span>
            </Link>
          )}
          <div className="sort-dropdown" ref={sortRef}>
            <button
              className="sort-dropdown-btn"
              onClick={() => setSortOpen((prev) => !prev)}
            >
              <span>Ordenar: {currentLabel}</span>
              <span className="dropdown-icon">{sortOpen ? '▲' : '▼'}</span>
            </button>
            {sortOpen && (
              <ul className="sort-dropdown-menu">
                {SORT_OPTIONS.map((opt) => (
                  <li
                    key={opt.value}
                    className={`sort-dropdown-item ${sort === opt.value ? 'active' : ''}`}
                    onClick={() => { setSort(opt.value); setSortOpen(false); }}
                  >
                    {opt.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <div className="product-grid">
        {list.length === 0 ? (
          <div className="product-grid-empty">Nenhum produto encontrado para os filtros selecionados.</div>
        ) : (
          list.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))
        )}
      </div>
    </section>
  );
};

export default ProductGrid;
